import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslate } from "react-admin";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import RefreshIcon from "@material-ui/icons/Refresh";
import ButtonFooter from "components/ButtonFooter";
import SnackbarUtils from "utils/SnackbarUtils";
import { updateGblParams } from "services/setting";
import {
  DEFAULT_AI_BASE_URL,
  ensureAiAuthorization,
  getAiBaseUrl,
  getAiBankCodes,
  getAiSettingContext,
  getAiSettings,
  getAiTokenStatus,
  normalizeAiBaseUrl,
  setAiBaseUrl,
  updateAiSettings,
} from "services/aiSetting";

const useStyles = makeStyles((theme) => ({
  tabPanel: { width: "100%", margin: "0 20px", minWidth: 0 },
  page: { paddingBottom: theme.spacing(10) },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  card: { marginBottom: theme.spacing(2), borderRadius: 12 },
  sectionTitle: { fontWeight: 700, marginBottom: theme.spacing(0.5) },
  muted: { color: theme.palette.text.secondary },
  context: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
  },
  rule: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
  },
  ruleHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
  },
  fullWidth: { width: "100%" },
  helperContent: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    width: "100%",
  },
  statusRow: { display: "flex", gap: theme.spacing(1), flexWrap: "wrap" },
  settingToggle: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    height: "100%",
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
  },
  autoPostHints: { marginTop: theme.spacing(0.5) },
  autoPostOnHint: { color: theme.palette.success.dark },
  autoPostOffHint: { color: theme.palette.warning.dark },
  fixedActions: {
    position: "fixed",
    bottom: "env(safe-area-inset-bottom, 0px)",
    zIndex: theme.zIndex.appBar,
    visibility: "hidden",
  },
}));

const emptyRule = () => ({
  bank_code: "",
  bank_sender_email: "",
  filename_patterns_text: ".pdf",
  pdf_password: "",
  is_active: true,
});

const toFormRule = (rule = {}) => ({
  bank_code: rule.bank_code || "",
  bank_sender_email: rule.bank_sender_email || "",
  filename_patterns_text: (rule.filename_patterns || []).join(", ") || ".pdf",
  pdf_password: "",
  has_password: Boolean(rule.has_password),
  is_active: rule.is_active !== false,
});

const toSettingsForm = (settings = {}) => ({
  ai_base_url: settings.ai_base_url || DEFAULT_AI_BASE_URL,
  ai_base_url_error: "",
  enabled: Boolean(settings.enabled),
  auto_post: Boolean(settings.auto_post),
  owner_emails_text: (settings.owner_emails || []).join(", "),
  tax_ids_text: (settings.tax_ids || []).join(", "),
  rules: (settings.rules || []).map(toFormRule),
});

const splitValues = (value) =>
  (value || "")
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean);

const normalizeBankCode = (bankCode) => (bankCode || "").trim().toUpperCase();

const getApiErrorMessage = (error) => {
  const data = error?.response?.data;
  if (Array.isArray(data?.errors) && data.errors.length) {
    return data.errors.map((item) => item.message).filter(Boolean).join("<br/>");
  }
  if (Array.isArray(data?.detail)) {
    return data.detail.map((item) => item.msg).filter(Boolean).join("<br/>");
  }
  return data?.detail || data?.message || error?.message || "Request failed";
};

const AISetting = ({ value, index }) => {
  const classes = useStyles();
  const translate = useTranslate();
  const context = getAiSettingContext();
  const tabPanelRef = useRef(null);
  const fixedActionsRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState(toSettingsForm());
  const [bankCodes, setBankCodes] = useState([]);

  const loadData = useCallback(async ({ refreshOnly = false } = {}) => {
    if (!context.bu) {
      SnackbarUtils.error(translate("ra.ai.missingBu"));
      return;
    }
    setLoading(true);
    try {
      const aiBaseUrl = await getAiBaseUrl({ refresh: true });
      if (refreshOnly) {
        const [, currentSettings] = await Promise.all([
          getAiTokenStatus(),
          getAiSettings(),
        ]);
        const settingsWithBaseUrl = { ...currentSettings, ai_base_url: aiBaseUrl };
        setSettings(settingsWithBaseUrl);
        setForm(toSettingsForm(settingsWithBaseUrl));
        setLoaded(true);
        return;
      }

      const authorization = await ensureAiAuthorization();
      const [banks, currentSettings] = await Promise.all([
        authorization.bankCodes || getAiBankCodes(),
        getAiSettings(),
      ]);
      const settingsWithBaseUrl = { ...currentSettings, ai_base_url: aiBaseUrl };
      setBankCodes(banks);
      setSettings(settingsWithBaseUrl);
      setForm(toSettingsForm(settingsWithBaseUrl));
      setLoaded(true);
    } catch (error) {
      SnackbarUtils.error(getApiErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [context.bu, translate]);

  useEffect(() => {
    if (value === index && !loaded) loadData();
  }, [index, loadData, loaded, value]);

  useLayoutEffect(() => {
    if (value !== index) return undefined;

    const tabPanel = tabPanelRef.current;
    const fixedActions = fixedActionsRef.current;
    if (!tabPanel || !fixedActions) return undefined;

    const syncActionBarBounds = () => {
      const bounds = tabPanel.getBoundingClientRect();
      const viewportWidth = document.documentElement.clientWidth;
      fixedActions.style.left = `${Math.max(bounds.left, 0)}px`;
      fixedActions.style.right = `${Math.max(viewportWidth - Math.min(bounds.right, viewportWidth), 0)}px`;
      fixedActions.style.visibility = "visible";
    };

    syncActionBarBounds();
    window.addEventListener("resize", syncActionBarBounds);

    const resizeObserver = window.ResizeObserver
      ? new window.ResizeObserver(syncActionBarBounds)
      : null;
    resizeObserver?.observe(tabPanel);

    return () => {
      window.removeEventListener("resize", syncActionBarBounds);
      resizeObserver?.disconnect();
    };
  }, [index, loaded, value]);

  const changeRule = (ruleIndex, field, nextValue) => {
    setForm((current) => ({
      ...current,
      rules: current.rules.map((rule, idx) =>
        idx === ruleIndex ? { ...rule, [field]: nextValue } : rule,
      ),
    }));
  };

  const saveSettings = async () => {
    const aiBaseUrl = normalizeAiBaseUrl(form.ai_base_url);
    if (!aiBaseUrl) {
      setForm((current) => ({ ...current, ai_base_url_error: translate("ra.ai.baseUrlRequired") }));
      return;
    }
    try {
      const parsedBaseUrl = new URL(aiBaseUrl);
      if (!["http:", "https:"].includes(parsedBaseUrl.protocol)) throw new Error();
    } catch (error) {
      setForm((current) => ({ ...current, ai_base_url_error: translate("ra.ai.baseUrlInvalid") }));
      return;
    }
    setForm((current) => ({ ...current, ai_base_url_error: "" }));

    const rules = form.rules.map((rule) => ({
      bank_code: rule.bank_code || null,
      bank_sender_email: rule.bank_sender_email.trim() || null,
      filename_patterns: splitValues(rule.filename_patterns_text),
      ...(rule.pdf_password.trim()
        ? { pdf_password: rule.pdf_password.trim() }
        : {}),
      is_active: Boolean(rule.is_active),
    }));
    if (rules.some((rule) => rule.filename_patterns.length === 0)) {
      SnackbarUtils.warning(translate("ra.ai.patternRequired"));
      return;
    }
    const selectedBankCodes = [];
    rules.forEach((rule) => {
      const bankCode = normalizeBankCode(rule.bank_code);
      if (bankCode) selectedBankCodes.push(bankCode);
    });
    if (new Set(selectedBankCodes).size !== selectedBankCodes.length) {
      SnackbarUtils.warning(translate("ra.ai.bankCodeUnique"));
      return;
    }
    const taxIds = splitValues(form.tax_ids_text);
    if (form.enabled && taxIds.length === 0) {
      SnackbarUtils.warning(translate("ra.ai.taxIdRequired"));
      return;
    }

    setSaving(true);
    const previousAiBaseUrl = await getAiBaseUrl();
    let baseUrlSaved = false;
    setAiBaseUrl(aiBaseUrl);
    try {
      await updateGblParams({
        Sections: "Setup",
        KeyField: "AI_BaseUrl",
        DataValue: aiBaseUrl,
        UserModified: localStorage.getItem("UserName"),
      });
      baseUrlSaved = true;

      const updated = await updateAiSettings({
        enabled: form.enabled,
        auto_post: form.auto_post,
        owner_emails: splitValues(form.owner_emails_text),
        tax_ids: taxIds,
        rules,
      });
      const settingsWithBaseUrl = { ...updated, ai_base_url: aiBaseUrl };
      setSettings(settingsWithBaseUrl);
      setForm(toSettingsForm(settingsWithBaseUrl));
      SnackbarUtils.success(translate("ra.ai.settingsSaved"));
    } catch (error) {
      if (!baseUrlSaved) setAiBaseUrl(previousAiBaseUrl);
      SnackbarUtils.error(getApiErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const copyIngestAddress = async () => {
    if (!settings?.ingest_address) return;
    try {
      await navigator.clipboard.writeText(settings.ingest_address);
      SnackbarUtils.success(translate("ra.ai.copied"));
    } catch (error) {
      SnackbarUtils.error(error.message);
    }
  };

  return (
    <div
      ref={tabPanelRef}
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      className={classes.tabPanel}
    >
      {value === index && (
        <div className={classes.page}>
          <div className={classes.header}>
            <div>
              <Typography variant="h6">{translate("ra.ai.title")}</Typography>
              <Typography variant="body2" className={classes.muted}>
                {translate("ra.ai.subtitle")}
              </Typography>
            </div>
            <IconButton
              onClick={() => loadData({ refreshOnly: true })}
              disabled={loading}
              aria-label="refresh"
            >
              <RefreshIcon />
            </IconButton>
          </div>

          <Paper variant="outlined" className={classes.context}>
            <Typography variant="caption" display="block">URI: {context.uri}</Typography>
            <Typography variant="caption" display="block">BU: {context.bu || "-"}</Typography>
          </Paper>

          {loading && !loaded ? (
            <Box display="flex" justifyContent="center" p={6}><CircularProgress /></Box>
          ) : (
            <>
              <Card variant="outlined" className={classes.card}>
                <CardContent>
                  <Typography className={classes.sectionTitle}>{translate("ra.ai.serviceStatus")}</Typography>
                  <Box className={classes.statusRow} mb={2}>
                    <Chip
                      color={settings?.status?.ready ? "primary" : "default"}
                      label={settings?.status?.ready ? translate("ra.ai.ready") : translate("ra.ai.notReady")}
                    />
                    {(settings?.status?.blockers || []).map((blocker) => (
                      <Chip key={blocker} label={blocker} variant="outlined" />
                    ))}
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <TextField
                        fullWidth variant="outlined" size="small"
                        label={translate("ra.ai.ingestAddress")}
                        helperText={translate("ra.ai.ingestAddressHint")}
                        value={settings?.ingest_address || ""}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Button
                        fullWidth variant="contained" color="primary" startIcon={<FileCopyOutlinedIcon />}
                        disabled={!settings?.ingest_address} onClick={copyIngestAddress}
                      >
                        {translate("ra.ai.copy")}
                      </Button>
                    </Grid>
                  </Grid>
                  {settings?.gmail_confirm?.code && (
                    <Box mt={2}>
                      <Typography variant="body2">
                        {translate("ra.ai.gmailCode")}: <strong>{settings.gmail_confirm.code}</strong>
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>

              <Card variant="outlined" className={classes.card}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <div style={{ marginBottom: 10 }}>
                      <Typography className={classes.sectionTitle}>{translate("ra.ai.settingsTitle")}</Typography>
                      <Typography variant="body2" className={classes.muted}>{translate("ra.ai.settingsHint")}</Typography>
                    </div>
                  </Box>
                  <Divider />
                  <Box mt={2}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          required
                          type="url"
                          variant="outlined"
                          size="small"
                          label={translate("ra.ai.baseUrl")}
                          helperText={form.ai_base_url_error || translate("ra.ai.baseUrlHint")}
                          error={Boolean(form.ai_base_url_error)}
                          value={form.ai_base_url}
                          onChange={(event) => setForm((current) => ({
                            ...current,
                            ai_base_url: event.target.value,
                            ai_base_url_error: "",
                          }))}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <div className={classes.settingToggle}>
                          <div>
                            <Typography variant="subtitle2">{translate("ra.ai.enabled")}</Typography>
                            <Typography variant="body2" className={classes.muted}>
                              {translate("ra.ai.enabledHint")}
                            </Typography>
                          </div>
                          <Switch
                            color="primary" checked={form.enabled}
                            inputProps={{ "aria-label": translate("ra.ai.enabled") }}
                            onChange={(event) => setForm((current) => ({ ...current, enabled: event.target.checked }))}
                          />
                        </div>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <div className={classes.settingToggle}>
                          <div>
                            <Typography variant="subtitle2">{translate("ra.ai.autoPost")}</Typography>
                            <div className={classes.autoPostHints}>
                              <Typography variant="body2" className={classes.autoPostOnHint}>
                                {translate("ra.ai.autoPostOnHint")}
                              </Typography>
                              <Typography variant="body2" className={classes.autoPostOffHint}>
                                {translate("ra.ai.autoPostOffHint")}
                              </Typography>
                            </div>
                          </div>
                          <Switch
                            color="primary" checked={form.auto_post}
                            inputProps={{ "aria-label": translate("ra.ai.autoPost") }}
                            onChange={(event) => setForm((current) => ({ ...current, auto_post: event.target.checked }))}
                          />
                        </div>
                      </Grid>
                    </Grid>
                  </Box>
                  <Box mt={2}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth multiline rows={2} variant="outlined"
                          label={translate("ra.ai.ownerEmails")}
                          helperText={translate("ra.ai.commaSeparated")}
                          value={form.owner_emails_text}
                          onChange={(event) => setForm((current) => ({ ...current, owner_emails_text: event.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth required={form.enabled} multiline rows={2} variant="outlined"
                          label={translate("ra.ai.taxIds")}
                          helperText={(
                            <span className={classes.helperContent}>
                              <span>{translate("ra.ai.taxIdHint")}</span>
                              <strong>{form.tax_ids_text.length}</strong>
                            </span>
                          )}
                          value={form.tax_ids_text}
                          onChange={(event) => setForm((current) => ({ ...current, tax_ids_text: event.target.value }))}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  <Box mt={3} display="flex" justifyContent="space-between" alignItems="center">
                    <Typography className={classes.sectionTitle}>{translate("ra.ai.rules")}</Typography>
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => setForm((current) => ({ ...current, rules: [...current.rules, emptyRule()] }))}
                    >
                      {translate("ra.ai.addRule")}
                    </Button>
                  </Box>

                  {form.rules.map((rule, ruleIndex) => (
                    <Paper key={ruleIndex} variant="outlined" className={classes.rule}>
                      <div className={classes.ruleHeader}>
                        <Typography variant="subtitle2">
                          {translate("ra.ai.ruleNumber", { number: ruleIndex + 1 })}
                        </Typography>
                        <IconButton
                          size="small" aria-label="delete rule"
                          onClick={() => setForm((current) => ({
                            ...current,
                            rules: current.rules.filter((unusedRule, idx) => idx !== ruleIndex),
                          }))}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </div>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <FormControl variant="outlined" size="small" className={classes.fullWidth}>
                            <InputLabel>{translate("ra.ai.bank")}</InputLabel>
                            <Select
                              value={rule.bank_code}
                              onChange={(event) => changeRule(ruleIndex, "bank_code", event.target.value)}
                              label={translate("ra.ai.bank")}
                            >
                              {bankCodes.map((bank) => (
                                <MenuItem
                                  key={bank.code}
                                  value={bank.code}
                                  disabled={form.rules.some(
                                    (otherRule, otherRuleIndex) =>
                                      otherRuleIndex !== ruleIndex &&
                                      normalizeBankCode(otherRule.bank_code) === normalizeBankCode(bank.code),
                                  )}
                                >
                                  {bank.code} - {bank.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth variant="outlined" size="small" type="email"
                            label={translate("ra.ai.senderEmail")} value={rule.bank_sender_email}
                            onChange={(event) => changeRule(ruleIndex, "bank_sender_email", event.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth variant="outlined" size="small" type="password" autoComplete="new-password"
                            label={translate("ra.ai.pdfPassword")} value={rule.pdf_password}
                            placeholder={rule.has_password ? translate("ra.ai.passwordUnchanged") : ""}
                            onChange={(event) => changeRule(ruleIndex, "pdf_password", event.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={9}>
                          <TextField
                            fullWidth required variant="outlined" size="small"
                            label={translate("ra.ai.filenamePatterns")}
                            helperText={translate("ra.ai.commaSeparated")}
                            value={rule.filename_patterns_text}
                            onChange={(event) => changeRule(ruleIndex, "filename_patterns_text", event.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <FormControlLabel
                            control={
                              <Switch
                                color="primary" checked={rule.is_active}
                                onChange={(event) => changeRule(ruleIndex, "is_active", event.target.checked)}
                              />
                            }
                            label={translate("ra.ai.active")}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}

                </CardContent>
              </Card>
              <div ref={fixedActionsRef} className={classes.fixedActions}>
                <ButtonFooter
                  disabled={saving || loading}
                  SaveFnc={saveSettings}
                  CancelFnc={() => setForm(toSettingsForm(settings))}
                  saveTitle={translate("ra.ai.saveSettings")}
                  cancelTitle={translate("ra.ai.cancel")}
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AISetting;
